<template>
  <div class="gv" :class="{ 'gv--compact': compact }">
    <div class="gv__bar">
      <button type="button" class="gv__chip" @click="scrollToToday(true)">
        <v-icon x-small>mdi-crosshairs-gps</v-icon>
        {{ $t('taskDeskToday') }}
      </button>
      <div class="gv__seg">
        <button
          v-for="z in zooms"
          :key="z.id"
          type="button"
          :class="{ on: zoom === z.id }"
          @click="setZoom(z.id)"
        >
          {{ $t(z.label) }}
        </button>
      </div>
      <v-spacer />
      <v-menu v-if="unscheduled.length" offset-y left max-height="320">
        <template v-slot:activator="{ on }">
          <button type="button" class="gv__chip" v-on="on">
            <v-icon x-small>mdi-calendar-remove-outline</v-icon>
            {{ $t('taskDeskUnscheduled') }} ({{ unscheduled.length }})
          </button>
        </template>
        <v-list dense>
          <v-list-item v-for="t in unscheduled" :key="t.key" @click="desk.editTask('task', t.el.ref)">
            <span class="gv__menu-dot" :style="{ background: t.col.color }" />
            <v-list-item-title>{{ t.el.ref.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div v-if="!groups.length" class="gv__empty">
      <v-icon size="36" color="grey lighten-1">mdi-chart-gantt</v-icon>
      <span>{{ $t('taskDeskNoDates') }}</span>
    </div>

    <div v-else ref="scroll" class="gv__scroll">
      <div class="gv__inner" :style="{ width: nameW + days * dayW + 'px' }">
        <!-- header -->
        <div class="gv__head">
          <div class="gv__corner" :style="{ width: nameW + 'px' }">{{ $t('taskDeskTaskName') }}</div>
          <div class="gv__timeline" :style="{ width: days * dayW + 'px' }">
            <div
              v-for="m in months"
              :key="m.key"
              class="gv__month"
              :style="{ insetInlineStart: m.x + 'px', width: m.w + 'px' }"
            >
              <span>{{ m.label }}</span>
            </div>
            <div
              v-for="d in dayCells"
              :key="d.key"
              :ref="d.today ? 'todayCell' : null"
              class="gv__day"
              :class="{ 'gv__day--we': d.weekend, 'gv__day--today': d.today }"
              :style="{ insetInlineStart: d.x + 'px', width: dayW + 'px' }"
            >
              <span v-if="d.label">{{ d.label }}</span>
            </div>
          </div>
        </div>

        <!-- body -->
        <div class="gv__body">
          <div class="gv__bands" :style="{ insetInlineStart: nameW + 'px', width: days * dayW + 'px' }">
            <div
              v-for="d in weekendCells"
              :key="'we' + d.key"
              class="gv__band"
              :style="{ insetInlineStart: d.x + 'px', width: dayW + 'px' }"
            />
            <div class="gv__now" :style="{ insetInlineStart: todayX + dayW / 2 + 'px' }" />
          </div>

          <template v-for="g in groups">
            <div :key="'g' + g.col.id" class="gv__group" :style="{ '--cc': g.col.color || '#9aa4b2' }">
              <div class="gv__group-name" :style="{ width: nameW + 'px' }">
                <span class="gv__dot" />
                {{ g.col.name }}
                <span class="gv__group-count">{{ g.tasks.length }}</span>
              </div>
            </div>
            <div
              v-for="t in g.tasks"
              :key="t.key"
              class="gv__row"
            >
              <div
                class="gv__name"
                :style="{ width: nameW + 'px' }"
                :title="t.el.ref.title"
                @click="desk.editTask('task', t.el.ref)"
              >
                {{ t.el.ref.title }}
              </div>
              <div class="gv__track" :style="{ width: days * dayW + 'px' }">
                <div
                  class="gv__task"
                  :class="{ 'gv__task--done': t.done, 'gv__task--late': isLate(t), 'gv__task--narrow': barW(t) < 70 }"
                  :style="{
                    insetInlineStart: x(t.start) + 2 + 'px',
                    width: barW(t) - 4 + 'px',
                    '--bc': t.col.color || '#90a4ae',
                  }"
                  :title="tip(t)"
                  @click="desk.editTask('task', t.el.ref)"
                >
                  <span class="gv__task-label">{{ t.el.ref.title }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { today, dayKey, diffDays, isWeekend, loc, fmt } from '../deskDates'

const ZOOMS = [
  { id: 'week', label: 'taskDeskZoomWeek', w: 56 },
  { id: 'month', label: 'taskDeskZoomMonth', w: 30 },
  { id: 'quarter', label: 'taskDeskZoomQuarter', w: 12 },
]

export default {
  name: 'GanttView',
  inject: ['desk'],
  props: {
    compact: { type: Boolean, default: false },
  },
  data () {
    let zoom = 'month'
    try { zoom = localStorage.getItem('taskDeskGanttZoom') || 'month' } catch (e) {}
    return { zoom: ZOOMS.some(z => z.id === zoom) ? zoom : 'month' }
  },
  computed: {
    zooms () {
      return ZOOMS
    },
    dayW () {
      const w = ZOOMS.find(z => z.id === this.zoom).w
      return this.compact ? Math.max(10, Math.round(w * 0.8)) : w
    },
    nameW () {
      return this.compact ? 120 : 260
    },
    scheduled () {
      return this.desk.tasks.filter(t => t.start)
    },
    unscheduled () {
      return this.desk.tasks.filter(t => !t.start)
    },
    range () {
      const t = today()
      let start = t.clone().subtract(7, 'days')
      let end = t.clone().add(30, 'days')
      this.scheduled.forEach(task => {
        if (task.start.isBefore(start)) start = task.start.clone().subtract(3, 'days')
        if (task.end.isAfter(end)) end = task.end.clone().add(7, 'days')
      })
      // keep the timeline bounded (2 years)
      if (diffDays(end, start) > 730) start = end.clone().subtract(730, 'days')
      return { start, end }
    },
    days () {
      return diffDays(this.range.end, this.range.start) + 1
    },
    dayCells () {
      const jalali = this.desk.jalali
      const t = dayKey(today())
      const out = []
      const d = this.range.start.clone()
      for (let i = 0; i < this.days; i++) {
        const n = loc(d, jalali).date()
        let label = ''
        if (this.dayW >= 24) label = loc(d, jalali).format('D')
        else if (n === 1 || n % 7 === 1) label = loc(d, jalali).format('D')
        out.push({
          key: dayKey(d),
          x: i * this.dayW,
          label,
          weekend: isWeekend(d, jalali),
          today: dayKey(d) === t,
        })
        d.add(1, 'day')
      }
      return out
    },
    weekendCells () {
      return this.dayCells.filter(d => d.weekend)
    },
    months () {
      const jalali = this.desk.jalali
      const out = []
      let cur = null
      this.dayCells.forEach((d, i) => {
        const m = loc(this.range.start.clone().add(i, 'days'), jalali)
        const key = m.format('YYYY-MM')
        if (!cur || cur.key !== key) {
          cur = { key, x: d.x, w: 0, label: m.format(this.dayW >= 24 ? 'MMMM YYYY' : 'MMM YY') }
          out.push(cur)
        }
        cur.w += this.dayW
      })
      return out
    },
    todayX () {
      return this.x(today())
    },
    groups () {
      return this.desk.columns
        .map(col => ({
          col,
          tasks: this.scheduled
            .filter(t => t.col === col)
            .sort((a, b) => a.start.valueOf() - b.start.valueOf()),
        }))
        .filter(g => g.tasks.length)
    },
  },
  watch: {
    'groups.length' (n, o) {
      if (n && !o) this.$nextTick(() => this.scrollToToday(false))
    },
  },
  mounted () {
    this.$nextTick(() => this.scrollToToday(false))
  },
  methods: {
    x (m) {
      return diffDays(m, this.range.start) * this.dayW
    },
    barW (t) {
      return (diffDays(t.end, t.start) + 1) * this.dayW
    },
    isLate (t) {
      return !t.done && t.end.isBefore(today())
    },
    tip (t) {
      const j = this.desk.jalali
      const s = fmt(t.start, 'D MMM YYYY', j)
      const e = fmt(t.end, 'D MMM YYYY', j)
      return t.el.ref.title + '\n' + (s === e ? s : s + ' → ' + e) + '\n' + t.col.name
    },
    setZoom (id) {
      this.zoom = id
      try { localStorage.setItem('taskDeskGanttZoom', id) } catch (e) {}
      this.$nextTick(() => this.scrollToToday(false))
    },
    scrollToToday (smooth) {
      const cell = this.$refs.todayCell && this.$refs.todayCell[0]
      const scroll = this.$refs.scroll
      if (!cell || !scroll) return
      // Centre today's column without scrolling the page itself.
      const sr = scroll.getBoundingClientRect()
      const cr = cell.getBoundingClientRect()
      const delta = cr.left + cr.width / 2 - (sr.left + sr.width / 2) + (this.$vuetify.rtl ? this.nameW / 2 : -this.nameW / 2)
      scroll.scrollTo({ left: scroll.scrollLeft + delta, behavior: smooth ? 'smooth' : 'auto' })
    },
  },
}
</script>

<style scoped>
.gv {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 8px;
  overflow: hidden;
}
.theme--dark .gv {
  background: #1e1e1e;
  border-color: #333;
}
.gv__bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #e6e9ef;
}
.theme--dark .gv__bar {
  border-color: #333;
}
.gv__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #dfe3ea;
  font-size: 0.78rem;
  color: #4b5563;
}
.gv__chip .v-icon {
  color: inherit;
}
.gv__chip:hover {
  border-color: #b8c0cc;
}
.gv__seg {
  display: inline-flex;
  padding: 2px;
  background: #eef1f6;
  border-radius: 6px;
}
.theme--dark .gv__seg {
  background: #2a2a2a;
}
.gv__seg button {
  height: 26px;
  padding: 0 10px;
  border-radius: 5px;
  font-size: 0.76rem;
  color: #5b6475;
}
.gv__seg button.on {
  background: #fff;
  color: var(--v-primary-base);
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.theme--dark .gv__seg button.on {
  background: #1e1e1e;
}
.gv__menu-dot,
.gv__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-inline-end: 8px;
  flex: 0 0 auto;
}
.gv__dot {
  background: var(--cc);
}
.gv__empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #8a94a6;
  font-size: 0.85rem;
}
.gv__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
.gv__inner {
  position: relative;
  min-height: 100%;
}
.gv__head {
  position: sticky;
  top: 0;
  z-index: 4;
  display: flex;
  height: 52px;
  background: #f8f9fb;
  border-bottom: 1px solid #e6e9ef;
}
.theme--dark .gv__head {
  background: #191919;
  border-color: #333;
}
.gv__corner {
  position: sticky;
  inset-inline-start: 0;
  z-index: 2;
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  padding: 0 12px 8px;
  font-size: 0.75rem;
  color: #8a94a6;
  background: inherit;
  border-inline-end: 1px solid #e6e9ef;
}
.gv__timeline {
  position: relative;
  flex: 0 0 auto;
}
.gv__month {
  position: absolute;
  top: 0;
  height: 24px;
  border-inline-start: 1px solid #e6e9ef;
  font-size: 0.74rem;
  font-weight: 600;
  color: #374151;
  overflow: hidden;
}
.theme--dark .gv__month {
  color: #ddd;
  border-color: #333;
}
.gv__month span {
  position: sticky;
  inset-inline-start: 0;
  display: inline-block;
  padding: 4px 8px;
  white-space: nowrap;
}
.gv__day {
  position: absolute;
  top: 24px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: #6b7280;
}
.gv__day--we {
  color: #c2185b;
}
.gv__day--today span {
  min-width: 22px;
  height: 22px;
  line-height: 22px;
  border-radius: 11px;
  background: var(--v-primary-base);
  color: #fff;
  text-align: center;
  padding: 0 3px;
}
.gv__body {
  position: relative;
  padding-bottom: 24px;
}
.gv__bands {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
}
.gv__band {
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(194, 24, 91, 0.04);
}
.gv__now {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  margin-inline-start: -1px;
  background: var(--v-primary-base);
  opacity: 0.7;
}
.gv__group,
.gv__row {
  position: relative;
  z-index: 1;
  display: flex;
}
.gv__group {
  height: 32px;
  background: rgba(0, 0, 0, 0.025);
  border-bottom: 1px solid #eef0f4;
}
.gv__group-name {
  position: sticky;
  inset-inline-start: 0;
  z-index: 2;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 0 12px;
  font-size: 0.8rem;
  font-weight: 700;
  background: #f6f7f9;
  border-inline-start: 3px solid var(--cc);
  border-inline-end: 1px solid #e6e9ef;
}
.theme--dark .gv__group-name {
  background: #232323;
  border-inline-end-color: #333;
}
.gv__group-count {
  margin-inline-start: 6px;
  font-size: 0.7rem;
  font-weight: 400;
  color: #8a94a6;
}
.gv__row {
  height: 38px;
  border-bottom: 1px solid #f2f4f7;
}
.theme--dark .gv__row,
.theme--dark .gv__group {
  border-color: #2a2a2a;
}
.gv__row:hover .gv__name {
  background: #f3f6fb;
}
.gv__name {
  position: sticky;
  inset-inline-start: 0;
  z-index: 2;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 0 12px 0 15px;
  padding-inline-start: 15px;
  font-size: 0.8rem;
  background: #fff;
  border-inline-end: 1px solid #e6e9ef;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
}
.theme--dark .gv__name {
  background: #1e1e1e;
  border-color: #333;
}
.gv__track {
  position: relative;
  flex: 0 0 auto;
}
.gv__task {
  position: absolute;
  top: 7px;
  height: 24px;
  border-radius: 6px;
  background: var(--bc);
  color: #fff;
  font-size: 0.72rem;
  display: flex;
  align-items: center;
  padding: 0 8px;
  cursor: pointer;
  min-width: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
  transition: filter 0.15s, transform 0.15s;
}
.gv__task:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}
.gv__task--done {
  opacity: 0.55;
}
.gv__task--late {
  box-shadow: 0 0 0 2px #fff, 0 0 0 3.5px #e91e63;
}
.gv__task-label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
/* Narrow bars: put the title next to the bar instead of inside it. */
.gv__task--narrow .gv__task-label {
  position: absolute;
  inset-inline-start: calc(100% + 6px);
  color: #374151;
  overflow: visible;
}
.theme--dark .gv__task--narrow .gv__task-label {
  color: #ddd;
}
.gv--compact .gv__name,
.gv--compact .gv__group-name {
  font-size: 0.74rem;
  padding-inline-start: 10px;
}
</style>
